import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { ImageStatusStore } from '../lib/store';
import { ImageStatus } from '../types';

const store = new ImageStatusStore();

const getInitialState = (src?: string): ImageStatus => {
	const status = src ? store.get(src) : undefined;

	return status ?? 'pending';
};

export const useImageStatus = (src?: string) => {
	const [status, setStatus] = useState(() => getInitialState(src));
	const imageRef = useRef<HTMLImageElement | null>();

	const resetImageHandlers = () => {
		if (imageRef.current) {
			imageRef.current.onload = null;
			imageRef.current.onerror = null;
			imageRef.current = null;
		}
	};

	const loadImage = useCallback(() => {
		if (!src) {
			return;
		}

		const image = new Image();

		image.src = src;

		image.onload = () => {
			resetImageHandlers();
			setStatus('loaded');
			store.set(src, 'loaded');
		};

		image.onerror = () => {
			resetImageHandlers();
			setStatus('error');
			store.set(src, 'error');
		};

		imageRef.current = image;
	}, [src]);

	useLayoutEffect(() => {
		if (src && !store.get(src)) {
			loadImage();
		}

		return () => resetImageHandlers();
	}, [loadImage, src]);

	return {
		isError: status === 'error',
		isLoaded: status === 'loaded',
		isPending: status === 'pending',
		status,
	};
};
